import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { redirectToLogin } from "../utils/auth";
import { Loading } from "../Components/Loading/Loading";
import { useUser } from "../context/UserContext";

const tabs = [
  { heading: "Request Details", id: "requestDetails" },
  { heading: "Media Upload", id: "mediaUpload" },
];

// Shared 44px-high control so native inputs/selects line up with react-select.
const controlClass =
  "w-full h-11 border border-gray-300 px-3 rounded-lg text-xs bg-white focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed";

export const Form = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [requestType, setRequestType] = useState("");
  const [priority, setPriority] = useState("");
  const [issueAt, setIssueAt] = useState("Sandesh");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [priorities, setpriorities] = useState([]);
  const [requestOptions, setrequestOptions] = useState([]);
  const [issueAtOptions, setIssueAtOptions] = useState([]);
  const [deadline, setdeadline] = useState("");
  const [status, setstatus] = useState("");
  const [assign, setassign] = useState("Sandesh RCS NNI Support");
  const [submitting, setSubmitting] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  // Free-typed/pasted collaborator emails (comma-separated).
  const [collaborators, setCollaborators] = useState("");

  const authToken = localStorage.getItem("authToken");
  const navigate = useNavigate();

  const { user } = useUser();

  // RCS SLA deadline = creation time + duration, keyed by severity label:
  //   P0 → 4 hours, P1 → 8 hours, P2 → 1 day, P3 → 5 days.
  const RCS_SLA = {
    P0: { amount: 4, unit: "hours" },
    P1: { amount: 8, unit: "hours" },
    P2: { amount: 1, unit: "days" },
    P3: { amount: 5, unit: "days" },
  };

  useEffect(() => {
    if (!authToken) {
      redirectToLogin();
      return;
    }
    if (!user.loaded) return;

    const BASE = process.env.REACT_APP_BACKEND_URL;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    };

    const get = (url) =>
      fetch(url, { method: "GET", headers }).then((r) => {
        if (r.status === 401) { redirectToLogin(); return null; }
        return r.json();
      });

    // RCS external clients never fetch users — the assignee is fixed.
    // Config comes back rcs-tagged automatically (portalTag uses clientType).
    Promise.all([
      get(`${BASE}/API/V2/config/priorities`),
      get(`${BASE}/API/V2/config/requesttypes`),
      get(`${BASE}/API/V2/config/issue-at`),
    ])
      .then(([prioritiesData, requestTypesData, issueAtData]) => {
        if (!prioritiesData || !requestTypesData) return;
        const sortedPriorities = [...prioritiesData].sort(
          (a, b) => Number(a.value) - Number(b.value)
        );
        setpriorities(sortedPriorities);
        setrequestOptions(requestTypesData);
        setIssueAtOptions(issueAtData || []);
        setFormLoading(false);
      })
      .catch(console.error);
  }, [user.loaded]);

  useEffect(() => {
    if (!priority) return;
    const label = priorities.find((p) => String(p.value) === String(priority))?.key;
    const rule = RCS_SLA[label];
    if (!rule) return;

    setdeadline(moment().add(rule.amount, rule.unit).format("YYYY-MM-DDTHH:mm"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priority, priorities]);

  const fileSelectedHandler = (event) => {
    const incoming = Array.from(event.target.files);
    const combined = [...selectedFiles, ...incoming];
    if (combined.length > 10) {
      alert("You can upload a maximum of 10 files.");
    }
    setSelectedFiles(combined.slice(0, 10));
  };

  const removeFileHandler = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const save = async () => {
    if (!title || !description || !requestType || !priority || !issueAt) {
      alert("Please fill all required fields.");
      return;
    }

    setSubmitting(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("priority", priority);
    formData.append("deadline", deadline);
    formData.append("assign", assign);
    formData.append("requestType", requestType);
    formData.append("issueAt", issueAt);
    formData.append("org", "Rcs NNI");
    formData.append("viewToPartner", true);
    formData.append("externalCollaborators", collaborators);

    selectedFiles.forEach((file) => {
      formData.append(`files`, file);
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        },
      );

      const result = await response.json();
      if (response.ok) {
        alert("Ticket submitted successfully!");
        navigate("/dashboard");
      } else {
        alert(result.error || "Something went wrong.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred while saving the form.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user.loaded || formLoading) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col p-6 bg-jiojbm20 min-h-full">
      {/* Tab Bar */}
      <div className="flex items-center ml-1 gap-6 mt-4">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(index)}
            className="text-xs font-bold leading-snug transition-colors relative"
          >
            {tab.heading}
            {activeTab === index && (
              <div className="absolute -bottom-1 left-0 right-0 h-1 rounded-full bg-jiojbmpurple" />
            )}
          </button>
        ))}
        <div className="ml-auto flex items-center">
          <button
            className={`text-xs rounded-3xl px-3 py-1 font-bold tracking-tight leading-tight text-white ${
              submitting
                ? "opacity-50 cursor-not-allowed bg-gray-400"
                : "bg-jiojbmpurple hover:opacity-90"
            }`}
            type="button"
            onClick={submitting ? null : save}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Tab: Request Details */}
      {activeTab === 0 && (
        <div className="flex flex-1 flex-col gap-2 mt-6 ml-1 text-xs">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 grid flex-1 grid-cols-2 grid-rows-[auto_1fr] gap-x-6 gap-y-4">
            <div className="flex flex-col gap-1 col-span-2">
              <p>
                Title <span className="text-jiored1">*</span>
              </p>
              <textarea
                className="w-full px-3 py-2.5 border border-gray-300 resize-none rounded-lg text-xs focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple placeholder-gray-500"
                placeholder="Add title here..."
                value={title}
                rows={1}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1 h-full">
              <p>
                Description <span className="text-jiored1">*</span>
              </p>
              <textarea
                className="w-full flex-1 min-h-40 appearance-none border border-gray-300 px-3 py-2 resize-none rounded-lg text-xs focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple placeholder-gray-500"
                placeholder="What's the issue? Include IDs, timestamps, and logs where relevant."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-4 self-start">
              <div className="flex flex-col gap-1">
                <p>
                  Service Request Type <span className="text-jiored1">*</span>
                </p>
                <select
                  className={controlClass}
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value)}
                >
                  <option value="">--Select Request--</option>
                  {requestOptions.map((requestOption, index) => (
                    <option key={index} value={requestOption.value}>
                      {requestOption.key}
                    </option>
                  ))}
                </select>
              </div>
              {/* <div className="flex flex-col gap-1">
                <p>
                  Issue At <span className="text-jiored1">*</span>
                </p>
                <select
                  className={controlClass}
                  value={issueAt}
                  onChange={(e) => setIssueAt(e.target.value)}
                >
                   <option value="">--Select--</option> 
                  {issueAtOptions.map((o, index) => (
                    <option key={index} value={o.value}>
                      {o.key}
                    </option>
                  ))}
                </select>
              </div> */}
              <div className="flex flex-col gap-1">
                <p>
                  Severity <span className="text-jiored1">*</span>
                </p>
                <select
                  className={controlClass}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="">--Select Severity--</option>
                  {priorities.map((p, index) => (
                    <option key={index} value={p.value}>
                      {p.key}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <p>
                  Deadline <span className="text-jiored1">*</span>
                </p>
                <input
                  type="datetime-local"
                  className={controlClass}
                  value={deadline}
                  onChange={(e) => setdeadline(e.target.value)}
                  disabled
                />
              </div>
              <div className="flex flex-col gap-1">
                <p>
                  Status <span className="text-jiored1">*</span>
                </p>
                <select
                  disabled
                  className={controlClass}
                  value={status}
                  onChange={(e) => setstatus(e.target.value)}
                >
                  <option value="0">Draft</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <p>
                  Assign <span className="text-jiored1">*</span>
                </p>
                <select
                  className={controlClass}
                  value={assign}
                  onChange={(e) => setassign(e.target.value)}
                  disabled
                >
                  <option>{assign}</option>
                </select>
              </div>
              <div className="flex flex-col gap-1 col-span-2">
                <p>Collaborators</p>
                <input
                  type="text"
                  className={controlClass}
                  value={collaborators}
                  onChange={(e) => setCollaborators(e.target.value)}
                  placeholder="Paste collaborator emails, comma-separated"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Media Upload */}
      {activeTab === 1 && (
        <div className="flex flex-1 flex-col gap-2 mt-6 ml-1 text-xs">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1">
            <input
              className="block w-full text-xs text-gray-600 file:mr-3 file:cursor-pointer file:rounded-full file:border-0 file:bg-jiojbmpurple file:px-4 file:py-2 file:font-bold file:text-white hover:file:opacity-90"
              type="file"
              onChange={fileSelectedHandler}
              multiple
              accept=".xls,.xlsx,.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.json,.csv,.pcap,.pcapng,.mp4,.mov,.avi,.mkv,.webm,.m4v"
            />
            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <p className="font-semibold">Selected Files:</p>
                <ul>
                  {selectedFiles.map((file, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center border border-gray-300 p-2 px-3 my-1 rounded-lg"
                    >
                      <span>{file.name}</span>
                      <button
                        type="button"
                        className="text-jiored1 hover:text-red-700"
                        onClick={() => removeFileHandler(index)}
                      >
                        ❌
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
